import { useContext, useEffect, useMemo, useState } from "react";
import { getActiveLanguage, getTranslate } from "react-localize-redux";
import { useSelector } from "react-redux";

import { useGetGameAffiliatesQuery, useGetLocalGameCategoriesQuery } from "@api";
import { MainContext } from "@context";
import { getUser } from "@utils";

const selectTranslate = (state) => ({
	currency: state.balance.currency,
	translate: getTranslate(state.localize),
	currentLanguage: getActiveLanguage(state.localize).code,
	isContainerLoading: state.thirdParty.casinoContainerLoading,
});

const useController = ({
	onSelectFilter,
	history,
	activeTab,
	filterValues,
	onlySelectFilter,
	showCategoriesFilter,
	showProvidersFilter,
}) => {
	const [filters, setFilters] = useState([]);
	const [tab, setTab] = useState(activeTab);

	const { translate, currentLanguage, isContainerLoading, currency } =
		useSelector(selectTranslate);
	const { state: contextState } = useContext(MainContext);

	const isLive = useMemo(() => {
		let currentTab = tab?.toLowerCase();

		const tabQueryParam = new URLSearchParams(window.location.search).get("tab");

		if (tabQueryParam) {
			currentTab = tabQueryParam;
		}

		switch (currentTab) {
			case "casino":
				return false;
			case "livecasino":
				return true;
			default:
				return null;
		}
	}, [tab]);

	const {
		data: gameCategories,
		isLoading: isGameCategoriesLoading,
		isFetching: isGameCategoriesFetching,
	} = useGetLocalGameCategoriesQuery(
		{
			size: 1000,
			isActive: true,
		},
		{ skip: !showCategoriesFilter }
	);

	const {
		data: gameAffiliates,
		isLoading: isGameAffiliatesLoading,
		isFetching: isGameAffiliatesFetching,
	} = useGetGameAffiliatesQuery({ size: 1000, isLive, currency }, { skip: !showProvidersFilter });

	const serializeGameCategories = () => {
		return (gameCategories?.categories || [])
			.filter((category) => category.isLive === isLive)
			?.map((category) => ({
				key: category.name,
				value: category.localize
					? category.localize[currentLanguage || "en"]
					: category.name,
			}));
	};

	const serializeGameAffiliates = () => {
		return (gameAffiliates || [])
			?.filter((aff) => aff.affiliate !== "egt")
			.map((item) => ({
				key: item.affiliate,
				value: `${item.affiliate} (${item.count})`,
				count: item.count,
			}));
	};

	const handleSelectFilter = (key, value, query) => {
		const isFilterExists = filters?.some((item) => item?.section === key);

		if (!isFilterExists) {
			const newFilters = filters
				? [...filters, { ...value, section: key }]
				: [{ ...value, section: key }];

			setFilters(newFilters);
			onSelectFilter?.(newFilters);
		} else {
			const nextFilters = filters?.map((item) => {
				if (item?.section === key) {
					return { ...value, section: key };
				}
				return item;
			});

			setFilters(nextFilters);
			onSelectFilter?.(nextFilters);
		}

		if (!onlySelectFilter && query) {
			history.push(query);
		}
	};

	const handleAllGamesButtonClick = () => {
		let query = "/provider?key=allGames&name=all games&tab=";
		const tabMap = {
			"Casino": "casino",
			"LiveCasino": "livecasino",
		};

		query += tabMap[activeTab];

		history.push(query);
	};

	useEffect(() => {
		setFilters(filterValues);
	}, [filterValues]);

	useEffect(() => {
		setTab(activeTab);
	}, [activeTab]);

	return {
		helpers: { translate },
		state: {
			...contextState,
			filters,
			user: getUser(),
			categories: serializeGameCategories(),
			affiliates: serializeGameAffiliates(),
			isContainerLoading,
			isGameCategoriesLoading: isGameCategoriesFetching || isGameCategoriesLoading,
			isGameAffiliatesLoading: isGameAffiliatesFetching || isGameAffiliatesLoading,
		},
		handlers: {
			handleSelectFilter,
			handleAllGamesButtonClick,
		},
	};
};

export default useController;
